import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaCheckCircle, FaExclamationCircle, FaTimes, FaInfoCircle, FaExclamationTriangle } from 'react-icons/fa';

// Default theme styles (can be overridden by theme prop)
const defaultSuccessTheme = {
    container: "mb-4 p-3 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg flex items-center border-l-4 border-green-500 dark:border-green-600",
    icon: "h-5 w-5 mr-2 flex-shrink-0"
};

const defaultErrorTheme = {
    container: "mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg flex items-center border-l-4 border-red-500 dark:border-red-600",
    icon: "h-5 w-5 mr-2 flex-shrink-0"
};

// Componenti originali per compatibilità con il codice esistente
export const SuccessMessage = ({ message, theme }) => {
    if (!message) return null;
    const themeToUse = theme?.success || defaultSuccessTheme;
    
    return (
        <div className={themeToUse.container}>
            <FaCheckCircle className={themeToUse.icon} />
            <span>{message}</span>
        </div>
    );
};

export const ErrorMessage = ({ message, theme }) => {
    if (!message) return null;
    const themeToUse = theme?.error || defaultErrorTheme;
    
    return (
        <div className={themeToUse.container}>
            <FaExclamationCircle className={themeToUse.icon} />
            <span>{message}</span>
        </div>
    );
};

// ✅ COMPONENTE NOTIFICA MINIMAL E RAFFINATO
const ToastNotification = ({ notification, onRemove }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const [progress, setProgress] = useState(100);

    useEffect(() => {
        // Animazione di entrata più delicata
        const timer = setTimeout(() => setIsVisible(true), 50);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        // Progress bar e auto-remove
        if (notification.duration && notification.duration > 0) {
            const progressInterval = setInterval(() => {
                setProgress(prev => {
                    const newProgress = prev - (100 / (notification.duration / 50));
                    return newProgress <= 0 ? 0 : newProgress;
                });
            }, 50);

            const removeTimer = setTimeout(() => {
                handleRemove();
            }, notification.duration);

            return () => {
                clearInterval(progressInterval);
                clearTimeout(removeTimer);
            };
        }
    }, [notification.duration]);

    const handleRemove = () => {
        setIsRemoving(true);
        setTimeout(() => {
            onRemove(notification.id);
        }, 250);
    };

    const getThemeConfig = () => {
        switch (notification.type) {
            case 'success':
                return {
                    bg: 'bg-white/90 dark:bg-gray-800/90',
                    border: 'border-l-4 border-green-500',
                    icon: FaCheckCircle,
                    iconColor: 'text-green-500',
                    textColor: 'text-gray-800 dark:text-gray-200',
                    progressBg: 'bg-green-500',
                    shadow: 'shadow-lg shadow-black/10'
                };
            case 'error':
                return {
                    bg: 'bg-white/90 dark:bg-gray-800/90',
                    border: 'border-l-4 border-red-500',
                    icon: FaExclamationCircle,
                    iconColor: 'text-red-500',
                    textColor: 'text-gray-800 dark:text-gray-200',
                    progressBg: 'bg-red-500',
                    shadow: 'shadow-lg shadow-black/10'
                };
            case 'warning':
                return {
                    bg: 'bg-white/90 dark:bg-gray-800/90',
                    border: 'border-l-4 border-amber-500',
                    icon: FaExclamationTriangle,
                    iconColor: 'text-amber-500',
                    textColor: 'text-gray-800 dark:text-gray-200',
                    progressBg: 'bg-amber-500',
                    shadow: 'shadow-lg shadow-black/10'
                };
            case 'info':
                return {
                    bg: 'bg-white/90 dark:bg-gray-800/90',
                    border: 'border-l-4 border-blue-500',
                    icon: FaInfoCircle,
                    iconColor: 'text-blue-500',
                    textColor: 'text-gray-800 dark:text-gray-200',
                    progressBg: 'bg-blue-500',
                    shadow: 'shadow-lg shadow-black/10'
                };
            default:
                return {
                    bg: 'bg-white/90 dark:bg-gray-800/90',
                    border: 'border-l-4 border-gray-500',
                    icon: FaInfoCircle,
                    iconColor: 'text-gray-500',
                    textColor: 'text-gray-800 dark:text-gray-200',
                    progressBg: 'bg-gray-500',
                    shadow: 'shadow-lg shadow-black/10'
                };
        }
    };

    const config = getThemeConfig();
    const IconComponent = config.icon;

    return (
        <div
            className={`
                ${config.bg} ${config.border} ${config.shadow}
                relative overflow-hidden
                max-w-sm w-full rounded-lg p-4 mb-3
                transform transition-all duration-250 ease-out cursor-pointer
                ${isVisible && !isRemoving 
                    ? 'translate-x-0 opacity-100 scale-100' 
                    : '-translate-x-full opacity-0 scale-98'
                }
                hover:scale-[1.02] hover:shadow-xl hover:shadow-black/15
                backdrop-blur-md border-r border-t border-b border-gray-200/50 dark:border-gray-600/50
            `}
            style={{ 
                zIndex: 2147483647
            }}
            onClick={handleRemove}
            role="alert"
        >
            {/* Progress bar minimalista */}
            {notification.duration && (
                <div className="absolute bottom-0 left-0 h-0.5 bg-gray-200/30 dark:bg-gray-600/30 w-full">
                    <div 
                        className={`h-full ${config.progressBg} transition-all duration-75 ease-linear`}
                        style={{ width: `${progress}%` }}
                    />
                </div>
            )}
            
            {/* Contenuto principale */}
            <div className="flex items-start justify-between">
                <div className="flex items-start flex-1 min-w-0">
                    <div className="flex-shrink-0 mr-3 mt-0.5">
                        <IconComponent 
                            className={`h-4 w-4 ${config.iconColor}`}
                            aria-hidden="true" 
                        />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium leading-5 break-words ${config.textColor}`}>
                            {notification.message}
                        </p>
                    </div>
                </div>
                
                {/* Pulsante chiudi minimalista */}
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        handleRemove();
                    }}
                    className="flex-shrink-0 ml-3 p-1 rounded-md hover:bg-gray-100/80 dark:hover:bg-gray-700/80 active:bg-gray-200/80 dark:active:bg-gray-600/80 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-gray-400/50"
                    aria-label="Chiudi notifica"
                >
                    <FaTimes className="h-3 w-3 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300" />
                </button>
            </div>
        </div>
    );
};

// ✅ CONTAINER MINIMAL - POSIZIONATO IN BASSO A SINISTRA
export const ToastContainer = ({ notifications, onRemove }) => {
    if (!notifications || notifications.length === 0) return null;

    return createPortal(
        <div 
            className="fixed bottom-6 left-6 space-y-2 max-h-screen overflow-hidden pointer-events-none z-50"
            style={{ 
                zIndex: 2147483647,
                position: 'fixed',
                pointerEvents: 'none'
            }}
        >
            {notifications.map((notification, index) => (
                <div 
                    key={notification.id} 
                    className="pointer-events-auto"
                    style={{
                        // Animazione più delicata
                        animationDelay: `${index * 75}ms`
                    }}
                >
                    <ToastNotification
                        notification={notification}
                        onRemove={onRemove}
                    />
                </div>
            ))}
        </div>,
        document.body
    );
};

// Hook per utilizzare facilmente le notifiche toast
export const useToast = () => {
    const showSuccess = (message, duration = 4000) => {
        if (window.addNotification) {
            window.addNotification(message, 'success', duration);
        }
    };

    const showError = (message, duration = 5000) => {
        if (window.addNotification) {
            window.addNotification(message, 'error', duration);
        }
    };

    const showInfo = (message, duration = 4000) => {
        if (window.addNotification) {
            window.addNotification(message, 'info', duration);
        }
    };

    const showWarning = (message, duration = 4500) => {
        if (window.addNotification) {
            window.addNotification(message, 'warning', duration);
        }
    };

    return { showSuccess, showError, showInfo, showWarning };
};